import {useCallback, useEffect, useRef, useState} from 'react';
import {
  GetHomeReelsOptions,
  HomeReel,
  HomeReelFeedView,
  socialFeedApi,
} from '../../../../services/socialFeedApi';
import {isRequestCanceled} from '../utils';

const REEL_THUMBNAIL_POLL_INTERVAL_MS = 3000;
const REEL_THUMBNAIL_POLL_WINDOW_MS = 2 * 60 * 1000;
const HOME_REELS_PAGE_SIZE = 20;
const HOME_REELS_MAX_BACKGROUND_REFRESH_LIMIT = 50;
const HOME_REELS_SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_HOME_REELS_VIEW: HomeReelFeedView = 'all';

type PendingReelThumbnail = {
  previewImageUrl: string;
  registeredAt: number;
};

type FetchReelsRequestOptions = {
  append?: boolean;
  background?: boolean;
  cursor?: string;
  limit?: number;
  search?: string;
  view?: HomeReelFeedView;
  topic?: string;
};

const mergeUniqueReels = (currentReels: HomeReel[], nextReels: HomeReel[]) => {
  const seenReelIds = new Set(currentReels.map((reel) => reel.reelId));
  return [
    ...currentReels,
    ...nextReels.filter((reel) => {
      if (seenReelIds.has(reel.reelId)) {
        return false;
      }

      seenReelIds.add(reel.reelId);
      return true;
    }),
  ];
};

const mergeUpdatedReels = (currentReels: HomeReel[], nextReels: HomeReel[]) => {
  if (currentReels.length === 0 || nextReels.length === 0) {
    return currentReels;
  }

  const nextReelsById = new Map(nextReels.map((reel) => [reel.reelId, reel]));
  return currentReels.map((reel) => nextReelsById.get(reel.reelId) || reel);
};

export const useHomeReels = (token: string | null, topic?: string | null) => {
  const reelsRequestControllerRef = useRef<AbortController | null>(null);
  const backgroundReelsRequestControllerRef = useRef<AbortController | null>(null);
  const reelViewRequestControllerRef = useRef<AbortController | null>(null);
  const reelLikeRequestControllersRef = useRef<Record<string, AbortController>>({});
  const hasCountedSelectedReelViewRef = useRef(false);
  const pendingReelThumbnailsRef = useRef<Record<string, PendingReelThumbnail>>({});

  const [selectedView, setSelectedView] = useState<HomeReelFeedView>(DEFAULT_HOME_REELS_VIEW);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [reels, setReels] = useState<HomeReel[]>([]);
  const [reelsLoading, setReelsLoading] = useState(true);
  const [reelsError, setReelsError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreReels, setHasMoreReels] = useState(false);
  const [loadMoreReelsLoading, setLoadMoreReelsLoading] = useState(false);
  const [loadMoreReelsError, setLoadMoreReelsError] = useState<string | null>(null);
  const [failedReelThumbnails, setFailedReelThumbnails] = useState<Record<string, boolean>>({});
  const [selectedReel, setSelectedReel] = useState<HomeReel | null>(null);
  const [activeReelId, setActiveReelId] = useState<string | null>(null);
  const [reelPlayerError, setReelPlayerError] = useState<string | null>(null);
  const [likingReelIds, setLikingReelIds] = useState<Record<string, boolean>>({});
  const [pendingReelThumbnails, setPendingReelThumbnails] = useState<Record<string, PendingReelThumbnail>>({});

  const revokePendingThumbnailUrl = useCallback((previewImageUrl?: string) => {
    if (previewImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
  }, []);

  const commitPendingReelThumbnails = useCallback((nextPendingReelThumbnails: Record<string, PendingReelThumbnail>) => {
    pendingReelThumbnailsRef.current = nextPendingReelThumbnails;
    setPendingReelThumbnails(nextPendingReelThumbnails);
  }, []);

  const pruneExpiredPendingReelThumbnails = useCallback(() => {
    const currentPendingReelThumbnails = pendingReelThumbnailsRef.current;
    const nextPendingReelThumbnails = {...currentPendingReelThumbnails};
    const now = Date.now();
    let hasChanges = false;

    Object.entries(nextPendingReelThumbnails).forEach(([reelId, pendingReelThumbnail]) => {
      if ((now - pendingReelThumbnail.registeredAt) <= REEL_THUMBNAIL_POLL_WINDOW_MS) {
        return;
      }

      revokePendingThumbnailUrl(pendingReelThumbnail.previewImageUrl);
      delete nextPendingReelThumbnails[reelId];
      hasChanges = true;
    });

    if (hasChanges) {
      commitPendingReelThumbnails(nextPendingReelThumbnails);
    }
  }, [commitPendingReelThumbnails, revokePendingThumbnailUrl]);

  const syncPendingReelThumbnails = useCallback((nextReels: HomeReel[]) => {
    const currentPendingReelThumbnails = pendingReelThumbnailsRef.current;
    const nextPendingReelThumbnails = {...currentPendingReelThumbnails};
    let hasChanges = false;

    Object.entries(nextPendingReelThumbnails).forEach(([reelId, pendingReelThumbnail]) => {
      if ((Date.now() - pendingReelThumbnail.registeredAt) > REEL_THUMBNAIL_POLL_WINDOW_MS) {
        revokePendingThumbnailUrl(pendingReelThumbnail.previewImageUrl);
        delete nextPendingReelThumbnails[reelId];
        hasChanges = true;
        return;
      }

      const matchingReel = nextReels.find((reel) => reel.reelId === reelId);

      if (matchingReel?.thumbnailUrl) {
        revokePendingThumbnailUrl(pendingReelThumbnail.previewImageUrl);
        delete nextPendingReelThumbnails[reelId];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      commitPendingReelThumbnails(nextPendingReelThumbnails);
    }
  }, [commitPendingReelThumbnails, revokePendingThumbnailUrl]);

  const applyPendingThumbnail = useCallback((reel: HomeReel): HomeReel => {
    const pendingReelThumbnail = pendingReelThumbnails[reel.reelId];

    if (!pendingReelThumbnail || reel.thumbnailUrl) {
      return reel;
    }

    return {
      ...reel,
      thumbnailUrl: pendingReelThumbnail.previewImageUrl,
    };
  }, [pendingReelThumbnails]);

  const registerPendingReelThumbnail = useCallback((reelId: string, previewImageUrl: string) => {
    if (!reelId || !previewImageUrl) {
      return;
    }

    const existingPendingThumbnail = pendingReelThumbnailsRef.current[reelId];

    if (existingPendingThumbnail?.previewImageUrl && existingPendingThumbnail.previewImageUrl !== previewImageUrl) {
      revokePendingThumbnailUrl(existingPendingThumbnail.previewImageUrl);
    }

    commitPendingReelThumbnails({
      ...pendingReelThumbnailsRef.current,
      [reelId]: {
        previewImageUrl,
        registeredAt: Date.now(),
      },
    });
  }, [commitPendingReelThumbnails, revokePendingThumbnailUrl]);

  const createFeedRequestController = useCallback(() => {
    reelsRequestControllerRef.current?.abort();
    const controller = new AbortController();
    reelsRequestControllerRef.current = controller;
    return controller;
  }, []);

  const createBackgroundRequestController = useCallback(() => {
    backgroundReelsRequestControllerRef.current?.abort();
    const controller = new AbortController();
    backgroundReelsRequestControllerRef.current = controller;
    return controller;
  }, []);

  const performReelsFetch = useCallback(async ({
    append = false,
    background = false,
    cursor,
    limit = HOME_REELS_PAGE_SIZE,
    search = debouncedSearchQuery,
    view = selectedView,
    topic: requestTopic = topic || undefined,
  }: FetchReelsRequestOptions = {}) => {
    if (!token) {
      setSelectedReel(null);
      setReels([]);
      setReelsError(null);
      setReelsLoading(false);
      setFailedReelThumbnails({});
      setHasMoreReels(false);
      setNextCursor(null);
      setLoadMoreReelsError(null);
      setLoadMoreReelsLoading(false);
      setActiveReelId(null);
      setLikingReelIds({});
      Object.values(pendingReelThumbnailsRef.current).forEach((pendingReelThumbnail) => {
        revokePendingThumbnailUrl(pendingReelThumbnail.previewImageUrl);
      });
      commitPendingReelThumbnails({});
      return;
    }

    if (background && reelsRequestControllerRef.current) {
      return;
    }

    const controller = background ? createBackgroundRequestController() : createFeedRequestController();
    const requestSignal = controller.signal;
    const requestOptions: GetHomeReelsOptions = {
      cursor,
      limit,
      search: search || undefined,
      view,
      topic: requestTopic,
    };

    try {
      if (background) {
        pruneExpiredPendingReelThumbnails();
      } else if (append) {
        setLoadMoreReelsLoading(true);
        setLoadMoreReelsError(null);
      } else {
        setReelsLoading(true);
        setReelsError(null);
        setLoadMoreReelsError(null);
        setLoadMoreReelsLoading(false);
        setHasMoreReels(false);
        setNextCursor(null);
        setReels([]);
      }

      const response = await socialFeedApi.getHomeReels(token, requestOptions, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      let nextReelsSnapshot: HomeReel[] = response.reels;

      if (background) {
        setReels((currentReels) => {
          nextReelsSnapshot = mergeUpdatedReels(currentReels, response.reels);
          return nextReelsSnapshot;
        });
      } else if (append) {
        setReels((currentReels) => {
          nextReelsSnapshot = mergeUniqueReels(currentReels, response.reels);
          return nextReelsSnapshot;
        });
      } else {
        setReels(response.reels);
      }

      if (!background) {
        setNextCursor(response.nextCursor ?? null);
        setHasMoreReels(Boolean(response.nextCursor));
      }

      if (!background && !append) {
        setFailedReelThumbnails({});
      }

      syncPendingReelThumbnails(nextReelsSnapshot);
      setActiveReelId((currentActiveReelId) => (
        nextReelsSnapshot.some((reel) => reel.reelId === currentActiveReelId)
          ? currentActiveReelId
          : nextReelsSnapshot[0]?.reelId || null
      ));
      setSelectedReel((currentSelectedReel) => (
        currentSelectedReel
          ? nextReelsSnapshot.find((reel) => reel.reelId === currentSelectedReel.reelId) || null
          : currentSelectedReel
      ));
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      if (background) {
        return;
      }

      if (append) {
        setLoadMoreReelsError(error instanceof Error ? error.message : 'Failed to load more reels');
      } else {
        setReelsError(error instanceof Error ? error.message : 'Failed to load reels');
      }
    } finally {
      if (!requestSignal.aborted) {
        if (background) {
          backgroundReelsRequestControllerRef.current = null;
        } else if (append) {
          setLoadMoreReelsLoading(false);
        } else {
          setReelsLoading(false);
        }
      }

      if (!background && reelsRequestControllerRef.current === controller) {
        reelsRequestControllerRef.current = null;
      }
    }
  }, [
    commitPendingReelThumbnails,
    createBackgroundRequestController,
    createFeedRequestController,
    debouncedSearchQuery,
    pruneExpiredPendingReelThumbnails,
    revokePendingThumbnailUrl,
    selectedView,
    syncPendingReelThumbnails,
    token,
    topic,
  ]);

  const refreshReels = useCallback(async () => {
    await performReelsFetch({
      search: debouncedSearchQuery,
      view: selectedView,
    });
  }, [debouncedSearchQuery, performReelsFetch, selectedView]);

  const handleSelectedViewChange = useCallback((view: HomeReelFeedView) => {
    setSelectedView(view);
    setLoadMoreReelsError(null);
    setNextCursor(null);
    setHasMoreReels(false);
  }, []);

  const handleSearchQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
    setLoadMoreReelsError(null);
    setNextCursor(null);
    setHasMoreReels(false);
  }, []);

  const resetReelsFeed = useCallback(async () => {
    if (selectedView === DEFAULT_HOME_REELS_VIEW && searchQuery.trim().length === 0 && debouncedSearchQuery.length === 0) {
      await performReelsFetch({
        search: '',
        view: DEFAULT_HOME_REELS_VIEW,
      });
      return;
    }

    setSelectedView(DEFAULT_HOME_REELS_VIEW);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setLoadMoreReelsError(null);
    setNextCursor(null);
    setHasMoreReels(false);
  }, [debouncedSearchQuery, performReelsFetch, searchQuery, selectedView]);

  const loadMoreReels = useCallback(async () => {
    if (!token || !nextCursor || reelsLoading || loadMoreReelsLoading) {
      return;
    }

    await performReelsFetch({
      append: true,
      cursor: nextCursor,
      search: debouncedSearchQuery,
      view: selectedView,
    });
  }, [debouncedSearchQuery, loadMoreReelsLoading, nextCursor, performReelsFetch, reelsLoading, selectedView, token]);

  const handleReelThumbnailError = useCallback((reelId: string) => {
    setFailedReelThumbnails((currentFailedReelThumbnails) => ({
      ...currentFailedReelThumbnails,
      [reelId]: true,
    }));
  }, []);

  const openReelPlayer = useCallback((reel: HomeReel) => {
    setActiveReelId(reel.reelId);
    hasCountedSelectedReelViewRef.current = false;
    setReelPlayerError(null);
    setSelectedReel(reel);
  }, []);

  const closeReelPlayer = useCallback(() => {
    reelViewRequestControllerRef.current?.abort();
    reelViewRequestControllerRef.current = null;
    hasCountedSelectedReelViewRef.current = false;
    setReelPlayerError(null);
    setSelectedReel(null);
  }, []);

  const updateReelCommentCount = useCallback((reelId: string, commentCount: number) => {
    const normalizedCommentCount = Math.max(0, commentCount);

    setReels((currentReels) => currentReels.map((reel) => (
      reel.reelId === reelId
        ? {
            ...reel,
            commentCount: normalizedCommentCount,
          }
        : reel
    )));
    setSelectedReel((currentSelectedReel) => (
      currentSelectedReel?.reelId === reelId
        ? {
            ...currentSelectedReel,
            commentCount: normalizedCommentCount,
          }
        : currentSelectedReel
    ));
  }, []);

  const applyUpdatedReel = useCallback((updatedReel: HomeReel) => {
    setReels((currentReels) => currentReels.map((reel) => (
      reel.reelId === updatedReel.reelId ? updatedReel : reel
    )));
    setSelectedReel((currentSelectedReel) => (
      currentSelectedReel?.reelId === updatedReel.reelId ? updatedReel : currentSelectedReel
    ));
  }, []);

  const handleSelectedReelPlay = useCallback(async () => {
    if (!token || !selectedReel || hasCountedSelectedReelViewRef.current) {
      return;
    }

    if (!selectedReel.videoUrl) {
      setReelPlayerError('This reel is missing its video source.');
      return;
    }

    hasCountedSelectedReelViewRef.current = true;
    reelViewRequestControllerRef.current?.abort();
    const controller = new AbortController();
    reelViewRequestControllerRef.current = controller;

    try {
      setReelPlayerError(null);
      const response = await socialFeedApi.incrementHomeReelView(token, selectedReel.reelId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      applyUpdatedReel(response.reel);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      hasCountedSelectedReelViewRef.current = false;
      setReelPlayerError(error instanceof Error ? error.message : 'Failed to update reel views');
    } finally {
      if (reelViewRequestControllerRef.current === controller) {
        reelViewRequestControllerRef.current = null;
      }
    }
  }, [applyUpdatedReel, selectedReel, token]);

  const handleSelectedReelLike = useCallback(async () => {
    if (!token || !selectedReel) {
      return;
    }

    const reelId = selectedReel.reelId;
    reelLikeRequestControllersRef.current[reelId]?.abort();
    const controller = new AbortController();
    reelLikeRequestControllersRef.current[reelId] = controller;

    setLikingReelIds((currentLikingReelIds) => ({
      ...currentLikingReelIds,
      [reelId]: true,
    }));

    try {
      setReelPlayerError(null);
      const response = await socialFeedApi.toggleHomeReelLike(token, reelId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      applyUpdatedReel(response.reel);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setReelPlayerError(error instanceof Error ? error.message : 'Failed to update reel like');
    } finally {
      if (reelLikeRequestControllersRef.current[reelId] === controller) {
        delete reelLikeRequestControllersRef.current[reelId];
      }

      if (!controller.signal.aborted) {
        setLikingReelIds((currentLikingReelIds) => {
          const nextLikingReelIds = {...currentLikingReelIds};
          delete nextLikingReelIds[reelId];
          return nextLikingReelIds;
        });
      }
    }
  }, [applyUpdatedReel, selectedReel, token]);

  useEffect(() => {
    const debounceTimeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, HOME_REELS_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceTimeoutId);
    };
  }, [searchQuery]);

  useEffect(() => {
    void performReelsFetch({
      search: debouncedSearchQuery,
      view: selectedView,
    });
  }, [debouncedSearchQuery, performReelsFetch, selectedView]);

  useEffect(() => () => {
    reelsRequestControllerRef.current?.abort();
    backgroundReelsRequestControllerRef.current?.abort();
    reelViewRequestControllerRef.current?.abort();
    Object.values(reelLikeRequestControllersRef.current).forEach((reelLikeController) => reelLikeController.abort());
    reelLikeRequestControllersRef.current = {};
    Object.values(pendingReelThumbnailsRef.current).forEach((pendingReelThumbnail) => {
      revokePendingThumbnailUrl(pendingReelThumbnail.previewImageUrl);
    });
    pendingReelThumbnailsRef.current = {};
  }, [revokePendingThumbnailUrl]);

  useEffect(() => {
    if (!token || reelsLoading || reelsError) {
      return;
    }

    pruneExpiredPendingReelThumbnails();

    const shouldPollForBackendThumbnail = Object.entries(pendingReelThumbnailsRef.current).some(([reelId, pendingReelThumbnail]) => {
      if ((Date.now() - pendingReelThumbnail.registeredAt) > REEL_THUMBNAIL_POLL_WINDOW_MS) {
        return false;
      }

      const matchingReel = reels.find((reel) => reel.reelId === reelId);
      return Boolean(matchingReel && !matchingReel.thumbnailUrl);
    });

    if (!shouldPollForBackendThumbnail) {
      return;
    }

    const pollTimeoutId = window.setTimeout(() => {
      void performReelsFetch({
        background: true,
        limit: Math.min(Math.max(reels.length, HOME_REELS_PAGE_SIZE), HOME_REELS_MAX_BACKGROUND_REFRESH_LIMIT),
        search: debouncedSearchQuery,
        view: selectedView,
      });
    }, REEL_THUMBNAIL_POLL_INTERVAL_MS);

    return () => {
      window.clearTimeout(pollTimeoutId);
    };
  }, [debouncedSearchQuery, performReelsFetch, pruneExpiredPendingReelThumbnails, reels, reelsError, reelsLoading, selectedView, token]);

  const resolvedReels = reels.map(applyPendingThumbnail);
  const resolvedSelectedReel = selectedReel ? applyPendingThumbnail(selectedReel) : null;

  return {
    reels: resolvedReels,
    reelsLoading,
    reelsError,
    selectedView,
    searchQuery,
    hasMoreReels,
    loadMoreReelsLoading,
    loadMoreReelsError,
    failedReelThumbnails,
    selectedReel: resolvedSelectedReel,
    activeReelId,
    reelPlayerError,
    setSelectedView: handleSelectedViewChange,
    setSearchQuery: handleSearchQueryChange,
    setActiveReelId,
    refreshReels,
    resetReelsFeed,
    loadMoreReels,
    registerPendingReelThumbnail,
    handleReelThumbnailError,
    openReelPlayer,
    closeReelPlayer,
    handleSelectedReelPlay,
    handleSelectedReelLike,
    likingReelIds,
    setReelPlayerError,
    updateReelCommentCount,
  };
};
